import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map as mapTo, shareReplay, take } from 'rxjs/operators';
import { AuthService } from '@core/auth/auth.service';
import { UserService } from '@shared/services/user/user.service';
import { StorageService, StorageKeys } from '@shared/services/storage/storage.service';
import { TileCacheService } from '@shared/services/basemap/tile-cache.service';
import { createCachedTileLayer } from '@shared/services/basemap/cached-tile-layer';
import { BasemapAttachOptions, BasemapDefinition, BasemapId } from '@shared/models/basemap';
import { CustomerIds } from '@shared/models/customer-settings';
import { environment, baseUrl } from '@environments/environment';

declare const L: any;

/**
 * Single source of truth for the map background used across the app.
 *
 * Every map used to build its own L.tileLayer with its own copy of the
 * OpenStreetMap URL, attribution and zoom settings. They all call attach()
 * now, which adds the base layers and — where it makes sense — a switcher for
 * flipping between the street map and satellite imagery.
 *
 * Satellite is offered to the powerlines customer only; every other customer
 * gets the street map exactly as before. The layer is Maanmittauslaitos
 * (National Land Survey of Finland) orthophoto, served through /api/maptiles so
 * the API key stays on the server. That coverage is Finland only; Danish and
 * German customers would need their own providers, which is why this is a
 * registry rather than a second hardcoded URL.
 */
@Injectable({ providedIn: 'root' })
export class BasemapService {

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private storageService: StorageService,
    private tileCacheService: TileCacheService,
    private translateService: TranslateService,
  ) {
  }

  /** Resolved once per session rather than per map. */
  private customerId$?: Observable<number>;

  private readonly definitions: BasemapDefinition[] = [
    {
      id: BasemapId.Map,
      labelKey: 'map.basemap.map',
      urlTemplate: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      minZoom: 3,
      maxNativeZoom: 19,
      maxZoom: 25,
      detectRetina: true,
      cached: false,
    },
    {
      id: BasemapId.Satellite,
      labelKey: 'map.basemap.satellite',
      urlTemplate: `${baseUrl}/api/maptiles/ortokuva/{z}/{x}/{y}`,
      attribution: 'Orthophoto &copy; <a href="https://www.maanmittauslaitos.fi/">Maanmittauslaitos</a>, CC BY 4.0',
      minZoom: 3,
      // Measured against the open WMTS interface: level 18 is the deepest that
      // returns a tile, level 19 is a hard 404. Native imagery is 0.5 m/px, so
      // level 17 (~0.59 m/px at Finnish latitudes) is where real detail stops
      // and 18 is upstream-interpolated — drop this to 17 to cut tile volume
      // fourfold at almost no visible cost. Past maxNativeZoom Leaflet upscales
      // locally. Keep in step with MapTilesController::MAX_ZOOM.
      maxNativeZoom: 18,
      maxZoom: 25,
      // Retina would request level 19 and hit the 404 ceiling a zoom early.
      detectRetina: false,
      cached: true,
    },
  ];

  /**
   * Adds the base layers to a map, plus a layer switcher unless suppressed.
   *
   * minZoom/maxZoom overrides exist because the call sites historically
   * disagreed — the interrupt map allows 5–19, the planner maps 3–25.
   */
  attach(map: any, options: BasemapAttachOptions = {}): void {
    // The street map is allowed for every customer, so it goes on immediately
    // and the map is never blank while we work out whether satellite applies.
    const street = this.createLayer(this.getDefinition(BasemapId.Map), options);
    street._basemapId = BasemapId.Map;
    street.addTo(map);

    this.resolveCustomerId(options.customerId)
      .pipe(take(1))
      .subscribe(customerId => this.addSatelliteIfAllowed(map, options, street, customerId));
  }

  /**
   * Adds the satellite layer and the switcher, once we know who the user is.
   *
   * Deliberately resolved asynchronously: the field reporting dashboard builds
   * its map from a setTimeout in ngAfterViewInit, before /api/me has answered,
   * so anything that read the customer synchronously there would always see 0.
   */
  private addSatelliteIfAllowed(
    map: any,
    options: BasemapAttachOptions,
    street: any,
    customerId: number,
  ): void {
    if (!this.isSatelliteAvailable(customerId)) return;

    // The component may have torn the map down while /api/me was in flight.
    if (!map._container) return;

    const satellite = this.createLayer(this.getDefinition(BasemapId.Satellite), options);
    satellite._basemapId = BasemapId.Satellite;

    if (options.showSwitcher !== false) {
      const layersByLabel: Record<string, any> = {
        [this.translateService.instant('map.basemap.map')]: street,
        [this.translateService.instant('map.basemap.satellite')]: satellite,
      };

      L.control
        .layers(layersByLabel, null, { position: options.controlPosition ?? 'topright' })
        .addTo(map);

      map.on('baselayerchange', (event: any) => {
        if (event?.layer?._basemapId) {
          this.storageService.set(StorageKeys.BASEMAP_SELECTED, event.layer._basemapId);
        }
      });
    }

    if (this.getActiveId() === BasemapId.Satellite) {
      map.removeLayer(street);
      satellite.addTo(map);
    }
  }

  /**
   * The caller's value when it has one, otherwise the logged-in user's.
   * Treats 0 as unknown — customer ids start at 1, and 0 is the placeholder
   * components hold before their own /api/me call resolves.
   */
  private resolveCustomerId(customerId?: number): Observable<number> {
    if (customerId) return of(customerId);

    if (!this.customerId$) {
      this.customerId$ = this.userService.getUserInfo().pipe(
        mapTo(userInfo => userInfo?.customer_id ?? 0),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.customerId$;
  }

  private getDefinition(id: BasemapId): BasemapDefinition {
    return this.definitions.find(definition => definition.id === id)!;
  }

  /** The basemap the user last chose, defaulting to the street map. */
  getActiveId(): BasemapId {
    const stored = this.storageService.get<BasemapId>(StorageKeys.BASEMAP_SELECTED);

    return this.definitions.some(definition => definition.id === stored)
      ? stored as BasemapId
      : BasemapId.Map;
  }

  /**
   * Whether satellite imagery may be offered to this customer.
   *
   * Gated to the powerlines customer: they survey line corridors across open
   * country, where aerial imagery is worth far more than a street map. Every
   * other customer keeps the plain OpenStreetMap background they had before.
   */
  isSatelliteAvailable(customerId?: number): boolean {
    return environment.satelliteBasemap === true && customerId === CustomerIds.POWERLINES;
  }

  /** Frees the tiles held on the device. */
  clearTileCache(): Promise<void> {
    return this.tileCacheService.clear();
  }

  /** Tile count and estimated bytes, for a "stored maps" readout. */
  getTileCacheStats(): Promise<{ tiles: number; bytes: number | null }> {
    return this.tileCacheService.stats();
  }

  private createLayer(definition: BasemapDefinition, options: BasemapAttachOptions): any {
    const layerOptions = {
      attribution: definition.attribution,
      minZoom: options.minZoom ?? definition.minZoom,
      maxZoom: options.maxZoom ?? definition.maxZoom,
      maxNativeZoom: definition.maxNativeZoom,
      detectRetina: definition.detectRetina,
    };

    if (!definition.cached) {
      return L.tileLayer(definition.urlTemplate, layerOptions);
    }

    return createCachedTileLayer(definition.urlTemplate, {
      ...layerOptions,
      tileCache: this.tileCacheService,
      getAuthToken: () => this.authService.getJwtToken(),
    });
  }
}
